import * as React from "react";
import * as moment from "moment";
import { useDispatch } from "@teamprevent/admin-redux";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { Button, Form, Modal, Message } from "semantic-ui-react";
import { Input } from "@neworbit/simpleui-input";

import { ClinicLocationTypeName } from "@teamprevent/common/appointment/models";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import { Appointment } from "@teamprevent/common/calendar";

import { submitDidNotAttend as submitDidNotAttendAction, SUBMIT_DID_NOT_ATTEND } from "../actions";
import { ClinicDetails } from "../../calendar/clinics/models";

import { calendarApi } from "../../calendar/calendarApi";

import { OldDidNotAttendButton } from "./OldDidNotAttendButton";

interface Props {
    /**
     * @deprecated Can be removed when FeatureToggleNames.EnableAppointmentDNAFix is removed
     */
    customerId: string;
    ohCaseId: string;
    task: TaskModel | undefined;
}

export const NewDidNotAttendButton: React.FunctionComponent<Props> = ({ ohCaseId, task }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = React.useState<boolean>(false);
    const [comment, setComment] = React.useState<string | undefined>(undefined);
    const [openConfirmation, setOpenConfirmation] = React.useState<boolean>(false);
    const [appointmentOrClinic, setAppointmentOrClinic] = React.useState<Appointment | undefined>(undefined);

    const childAppointment = React.useMemo(() => {
        if (!appointmentOrClinic || !task)
        {
            return null;
        }

        return appointmentOrClinic.appointments?.find(a => a.id === task?.appointmentId);
    },[appointmentOrClinic, task]);

    React.useEffect(() => {
        const getAppointmentData = async () => {
            if (task?.appointmentId) {
                const data = await calendarApi.get(task?.appointmentId);
                setAppointmentOrClinic(data);
            }
        };

        if (appointmentOrClinic === undefined) {
            getAppointmentData();
        }
    }, [appointmentOrClinic, task?.appointmentId]);

    const provideComment = React.useCallback(() => setOpen(true), []);
    const onCommentSubmit = React.useCallback(() => setOpenConfirmation(true), []);

    const cancelDidNotAttend = React.useCallback(() => {
        setOpen(false);
        setComment(undefined);
    }, []);

    const onConfirmationClose = React.useCallback(() => setOpenConfirmation(false), []);

    const onConfirmationConfirm = React.useCallback(async () => {
        if (task === undefined || comment === undefined) { return; }
        setOpenConfirmation(false);

        setOpen(false);
        await dispatch(submitDidNotAttendAction(ohCaseId, task.id, comment));
        dispatch(navigateToParentRoute());
    }, [comment, dispatch, ohCaseId, task]);

    const isAppointmentEndInPast = React.useCallback(() => {
        if (childAppointment) {
            return moment(childAppointment.end).isBefore(moment());
        }

        // If the ids don't match then the appointment is in a clinic
        if (appointmentOrClinic && task && (appointmentOrClinic.id === task.appointmentId ||
            (appointmentOrClinic as ClinicDetails)?.clinicLocationTypes.includes(ClinicLocationTypeName.mobile)))
        {
            return moment(appointmentOrClinic.end).isBefore(moment());
        }

        return false;

    }, [appointmentOrClinic, childAppointment, task]);

    const canConfirmDna = isAppointmentEndInPast();

    if (!task || task?.completed) {
        return null;
    }

    return (
        <>
            <IsLoadingButton requestNames={SUBMIT_DID_NOT_ATTEND} onClick={provideComment} content="DNA" secondary />
            <Modal open={open} size="small" closeOnDimmerClick={false}>
                <Modal.Header>Provide comment</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Input.Textarea
                            label={"Comment"}
                            value={comment}
                            onChange={setComment}
                        />
                        <Message warning visible>These comments will be seen in the manager and employee portal.</Message>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={cancelDidNotAttend}>Cancel</Button>
                    <Button content="Save" onClick={onCommentSubmit} />
                </Modal.Actions>
            </Modal>
            <Modal open={openConfirmation} size="small">
                <Modal.Header>
                    DNA
                </Modal.Header>
                <Modal.Content>
                    <span>
                        {
                            canConfirmDna ? "If you set this appointment to a DNA you will be unable to complete this appointment " +
                                "and notifications of the DNA will be sent to the employee and any Managers linked " +
                                "to the case - are you sure you wish to continue?" :
                                "You cannot set this task to a DNA until after the end of the appointment."
                        }
                    </span>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={onConfirmationClose}>Cancel</Button>
                    {canConfirmDna &&
                        <Button
                            primary
                            onClick={onConfirmationConfirm}
                        >
                            Confirm
                        </Button>
                    }
                </Modal.Actions>
            </Modal>
        </>
    );
};

export const DidNotAttendButton = (props: Props) =>
    getFeatureToggles()[FeatureToggleNames.EnableAppointmentDNAFix]
        ? <NewDidNotAttendButton ohCaseId={props.ohCaseId} task={props.task} customerId={props.customerId} />
        // eslint-disable-next-line deprecation/deprecation
        : <OldDidNotAttendButton ohCaseId={props.ohCaseId} task={props.task} customerId={props.customerId} />;
